// React
import { useEffect, useState } from 'react';
// Custom Components
import Header from '../Header/Header';
import Button from '../Button/Button';
// Styles
import LoginStyle from './Login.module.scss';
// Next JS
import { useRouter } from 'next/router';
// React
import { useDispatch, useSelector } from 'react-redux';
//Actions
import userActions from '@/src/redux/user/actions';
import applicantsActions from '@/src/redux/applicants/actions';
// Selectors
import { getUserInfoSelector } from '@/src/redux/user/selectors';
import { getApplicantsSelector } from '@/src/redux/applicants/selectors';
// Constants
import StorageKey from '@/src/constants/StorageKey';
import { APPLICANTS_STATUS_CODE } from '@/src/constants/Common';
// Utils
import { clientLog, LOG_LEVEL } from '@/src/utils/clientLogger';
// Hooks
import { getDeviceInfo, openInLineApp, useLiff } from '@/src/hooks/Liff/liff';
import { useApplicantDetails } from '@/src/hooks/Application/applicationDetails';
import {
  isInsideLineApp,
  persistOpenExternalBrowserFromUrl,
  shouldOpenInLineApp,
} from '@/src/utils/lineBrowser';

export function Login() {
  const router = useRouter();
  const dispatch = useDispatch();

  const userInfo = useSelector(getUserInfoSelector);
  const applicants = useSelector(getApplicantsSelector);

  const [loadingLineLogin, setLoadingLineLogin] = useState(false);
  const [loadingApplicantsFetch, setLoadingApplicantsFetch] = useState(false);

  const { isLiffReady, isLiffLoggedIn, isInLiffClient, liffAppUrl, liffLogin, getLiffProfile } = useLiff();

  const [useLineAppLogin, setUseLineAppLogin] = useState(() => {
    if (typeof window === 'undefined') return false;

    persistOpenExternalBrowserFromUrl();
    return shouldOpenInLineApp(false);
  });

  useEffect(() => {
    persistOpenExternalBrowserFromUrl();
  }, []);

  useEffect(() => {
    if (!isLiffReady) return;
    setUseLineAppLogin(shouldOpenInLineApp(isInLiffClient));
  }, [isLiffReady, isInLiffClient]);

  const openLineApp = () => {
    if (!liffAppUrl) return;
    openInLineApp();
  };

  const initializeAndFetchProfile = async () => {
    setLoadingLineLogin(true);
    getLiffProfile()
        .then(async (profile) => {
          if (!profile?.displayName || !profile?.userId) {
            clientLog(LOG_LEVEL.ERROR, 'Profile missing required fields: ', `${!profile?.displayName ? 'Missing displayName. ' : ''}` + `${!profile?.userId ? 'Missing userId.' : ''}`);
            clientLog(LOG_LEVEL.ERROR, 'Profile missing required fields device details: ', getDeviceInfo());
            return;
          }

          const response = await fetch('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              username: profile.displayName,
              password: profile.userId,
              role: "ROLE_USER"
            })
          });

          if (response.ok) {
            const data = await response.json();
            const currentTime = new Date().getTime();
            localStorage.setItem(StorageKey.TOKEN, data.token);
            localStorage.setItem(StorageKey.AUTH_ROLE, "ROLE_USER");
            localStorage.setItem(StorageKey.GENERATED, currentTime.toString());

            const id = router.query.id as string;
            if (id) {
              router.replace(`?id=${id}`);
            } else {
              dispatch(userActions.getUserByLineId(profile.userId));
            }
          } else {
            clientLog(LOG_LEVEL.ERROR, 'Login failed: ', await response.text());
            clientLog(LOG_LEVEL.ERROR, 'Login failed device details: ', getDeviceInfo());
          }
        })
        .catch((err) => {
          clientLog(LOG_LEVEL.ERROR, 'Unexpected error:', err instanceof Error ? err.stack || err.message : String(err));
          clientLog(LOG_LEVEL.ERROR, 'Unexpected error device details: ', getDeviceInfo());
        });
  };

  const loginToLineLiff = () => {
    if (!router?.isReady || loadingLineLogin) return;

    if (isLiffLoggedIn && isLiffReady) {
      initializeAndFetchProfile();
    } else if (isInLiffClient) {
      // LIFF browser authenticates on init; liff.login() would open a nested window.
      return;
    } else {
      liffLogin();
    }
  }

  // Complete app login after LIFF auth inside the LINE app (or same-tab OAuth return).
  useEffect(() => {
    if (!isLiffReady || !router.isReady || loadingLineLogin) return;

    const hasAppSession = !!localStorage.getItem(StorageKey.TOKEN);
    if (hasAppSession || !isLiffLoggedIn) return;

    const pendingLineLogin = sessionStorage.getItem(StorageKey.LINE_LOGIN_PENDING);

    if (isInLiffClient || isInsideLineApp(isInLiffClient) || pendingLineLogin) {
      sessionStorage.removeItem(StorageKey.LINE_LOGIN_PENDING);
      initializeAndFetchProfile();
    }
  }, [isLiffLoggedIn, isLiffReady, isInLiffClient, router.isReady, loadingLineLogin]);

  useEffect(() => {
    setLoadingLineLogin(false);
  },[]);

  useEffect(() => {
    if (loadingLineLogin && userInfo != null && userInfo?.phoneNumber == null) {
      window.location.replace(`${window.location.origin}/registration`);
    } else if (loadingLineLogin && userInfo != null && userInfo?.phoneNumber != null) {
      dispatch(applicantsActions.fetchApplicationByPhoneNum(userInfo?.phoneNumber));
      setLoadingApplicantsFetch(true);
    }
  }, [userInfo]);

  useEffect(() => {
    if (applicants.applicationsByPhoneNum.length !== 0 && loadingApplicantsFetch == true) {
        setLoadingApplicantsFetch(false);
        const waitingForResponseApplication = applicants.applicationsByPhoneNum?.find((application: any) => application.applicationStatus ===   APPLICANTS_STATUS_CODE.WAITING_FOR_FORM_RESPONSE);
        if (waitingForResponseApplication === undefined) {
          router.push(`/applicant`);
        } else {
          router.push('/application-confirmation/' + waitingForResponseApplication.phoneNumber);
        }
    } else if (applicants.applicationsByPhoneNum.length === 0 && loadingApplicantsFetch === true && applicants.action?.includes("SUCCESS")){
      router.push(`/applicant`);
    }
  }, [applicants.applicationsByPhoneNum]);
  
  return (
    <div >
        <Header />
        <div className={ LoginStyle.login }>
          <div className={ LoginStyle['login-box'] }>
              <div className={ LoginStyle['login-box-label'] }>ログイン</div>
              {useLineAppLogin ? (
                <div className={LoginStyle['login-external']}>
                  <p className={LoginStyle['login-external-message']}>
                    LINEアプリからログインしてください。
                  </p>
                  <Button
                    className="mt-25"
                    title="LINEアプリで開く"
                    onClickEvent={openLineApp}
                    backgroundColor="#00B700"
                    width="100%"
                    icon={{ defaultIcon: '/images/ic-line.svg' }}
                    disabled={!liffAppUrl || loadingLineLogin}
                  />
                </div>
              ) : (
                <Button
                  className="mt-25"
                  title="LINEでログイン"
                  onClickEvent={() => loginToLineLiff()}
                  backgroundColor="#00B700"
                  width="100%"
                  icon={{ defaultIcon: '/images/ic-line.svg' }}
                  disabled={!isLiffReady || loadingLineLogin}
                />
              )}
          </div>
        </div>
    </div>
  );
}
